<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";
import { useRouteQuery } from "@vueuse/router";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableSkeleton } from "@/components/ui/table";
import TablePagination from "@/components/ui/table/TablePagination.vue";
import TicketTableRow from "./TicketTableRow.vue";
import { TicketData } from "@telegraphio/bapi-types";
import { TABLE_COLUMN_COUNT_KEY, TABLE_IS_FETCHING_KEY } from "@/types/symbols";

type Props = {
  customerId: string;
  tickets: TicketData[];
  reportingGroups: Record<string, string>;
  totalTickets: number;
  isLoading: boolean;
  isFetching: boolean;
};

const props = defineProps<Props>();
defineEmits(["refresh"]);

const sortColumn = useRouteQuery<string>("sort_by", "created_ts");
const sortDirection = useRouteQuery<"ASC" | "DESC">("sort_by_direction", "ASC");
const pageSize = useRouteQuery("page_size", "25", { transform: Number });
const pageNumber = useRouteQuery("page_number", "1", { transform: Number });
const totalPages = computed(() => (props.totalTickets === 0 ? 0 : Math.ceil(props.totalTickets / pageSize.value)));

const isFirstLoad = ref(true);

const columns: Array<{ label: string; value: string }> = [
  { label: "Priority", value: "priority" },
  { label: "Ticket", value: "id" },
  { label: "Ticket Name", value: "name" },
  { label: "Reporting Group", value: "reporting_groups" },
  { label: "Asset(s)", value: "asset_ids" },
  { label: "RR", value: "railroads" },
  { label: "L/E", value: "loaded_empty" },
  { label: "Tag(s)", value: "tags" },
  { label: "Created", value: "created_ts" },
  { label: "Last Action", value: "updated_ts" },
  { label: "Status", value: "state" },
  { label: "Assignee", value: "assignees" },
];

const columnWidths = computed(() => {
  const widths: Record<string, string> = {
    priority: "w-20",
    id: "w-32",
    name: "w-72",
    reporting_groups: "w-52",
    asset_ids: "w-32",
    railroads: "w-16",
    loaded_empty: "w-16",
    tags: "w-40",
    created_ts: "w-32",
    updated_ts: "w-32",
    state: "w-32",
    assignees: "w-40",
  };

  return columns.map((column) => widths[column.value] ?? "w-32");
});

// Provides the number of columns to the table skeleton and table row components
provide(TABLE_COLUMN_COUNT_KEY, columns.length);

// Provides the is fetching flag from the query
provide(
  TABLE_IS_FETCHING_KEY,
  computed(() => props.isLoading && !isFirstLoad.value),
);

function setSort(column: string) {
  if (column === sortColumn.value) {
    sortColumn.value = column;
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
    return sortDirection.value;
  }
  sortColumn.value = column;
  sortDirection.value = "ASC";
}

function handlePageChange(newPage: number) {
  pageNumber.value = newPage;
}

function handlePageSizeChange(newPageSize: number) {
  pageSize.value = newPageSize;
  if (props.totalTickets <= newPageSize) {
    pageNumber.value = 1;
  }
}

// Remove when we convert to use Tanstack Query
// TanStack Query will provide an `isFetching` flag
watch(
  () => props.isLoading,
  (newVal: boolean) => {
    // Only set isFirstLoad to false if the data has loaded and the first load is true
    if (newVal === false && isFirstLoad.value) {
      isFirstLoad.value = false;
    }
  },
);
</script>

<template>
  <section class="border-border rounded-md border">
    <Table class="table-fixed overflow-x-auto">
      <TableHeader class="bg-muted border-border sticky top-0 z-20 border-t">
        <TableRow :is-loading="false">
          <TableHead
            v-for="(column, index) in columns"
            :key="column.value"
            sortable
            :sorted="sortColumn === column.value ? (sortDirection === 'ASC' ? 'asc' : 'desc') : false"
            :on-sort="() => setSort(column.value)"
            :class="columnWidths[index]"
          >
            {{ column.label }}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="isLoading && isFirstLoad">
          <TableSkeleton />
        </template>
        <template v-else>
          <TicketTableRow
            v-for="ticket in tickets"
            :key="ticket.id"
            :ticket="ticket"
            :customer-id="customerId"
            :reporting-groups="
              ticket.reporting_groups ? ticket.reporting_groups.map((group) => reportingGroups[group]) : undefined
            "
            @assigned="$emit('refresh')"
          />
        </template>
      </TableBody>
    </Table>
    <TablePagination
      :server-pagination="{
        currentPage: pageNumber,
        pageSize: pageSize,
        totalCount: totalTickets,
        totalPages,
      }"
      @update:current-page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />
  </section>
</template>
